<template>
  <div class="content-source" :class="{ open }">
    <div class="cs-summary" @click="open = true">
      <span class="cs-summary-text">{{ summary }} <span class="cs-summary-link">Have existing content? Paste or import ▸</span></span>
    </div>

    <div class="cs-expanded">
      <div class="cs-seg">
        <button
          v-for="opt in OPTIONS"
          :key="opt.value"
          class="cs-opt"
          :class="{ selected: source.type === opt.value }"
          @click="select(opt.value)"
        >
          <span class="cs-opt-ico" v-html="opt.icon"></span>
          <span class="cs-opt-name">{{ opt.name }}</span>
        </button>
      </div>

      <p class="cs-helper">{{ helper }}</p>

      <div v-if="source.type === 'paste_existing'" class="cs-panel">
        <textarea
          class="cs-textarea"
          placeholder="Paste your existing article or page copy here - POP will rewrite and optimize it to hit your targets."
          v-model="source.pasted_html"
          @input="emit"
        ></textarea>
      </div>

      <div v-if="source.type === 'import_url'" class="cs-panel">
        <div class="cs-url-row">
          <input
            class="cs-url-input"
            type="url"
            placeholder="https://www.example.com/your-existing-page"
            v-model="source.import_url"
          />
          <button class="aiw2-btn" :disabled="fetching || !source.import_url" @click="$emit('fetch', source.import_url)">
            <span v-if="fetching" class="spin"></span>
            {{ fetching ? 'Fetching…' : (source.imported_word_count ? 'Re-fetch' : 'Fetch content') }}
          </button>
        </div>
        <div v-if="source.imported_word_count" class="cs-url-status ok">✓ Imported <strong>{{ importedCount }} words</strong> from {{ importHost }} - POP will optimize this content.</div>
        <div v-else-if="fetchError" class="cs-url-status err">{{ fetchError }}</div>
      </div>
    </div>
  </div>
</template>

<script>
const OPTIONS = [
  {
    value: "write_new", name: "Write new",
    helper: "POP writes a fresh article from your brief & recommendations.",
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v18M3 12h18"/></svg>',
  },
  {
    value: "paste_existing", name: "Paste existing",
    helper: "Paste copy you already have - POP rewrites & optimizes it to hit your targets.",
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>',
  },
  {
    value: "import_url", name: "Import from URL",
    helper: "Scrape a live page, then POP optimizes that content.",
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v12M7 10l5 5 5-5M5 21h14"/></svg>',
  },
];

export default {
  name: "AiwV2ContentSourceSection",
  props: {
    model: { type: Object, required: true },
    fetching: { type: Boolean, default: false },
    fetchError: { type: String, default: "" },
  },
  data() {
    return { OPTIONS, open: (this.model.content_source || {}).type !== "write_new" };
  },
  computed: {
    source() {
      return this.model.content_source;
    },
    helper() {
      return (OPTIONS.find((o) => o.value === this.source.type) || OPTIONS[0]).helper;
    },
    importedCount() {
      return Number(this.source.imported_word_count || 0).toLocaleString();
    },
    importHost() {
      try {
        return new URL(this.source.import_url).hostname.replace(/^www\./, "");
      } catch (e) {
        return "the page";
      }
    },
    summary() {
      if (this.source.type === "paste_existing") return "Optimizing pasted content.";
      if (this.source.type === "import_url") {
        return this.source.imported_word_count
          ? `Optimizing imported page content (${this.source.imported_word_count} words).`
          : "Optimizing imported page content.";
      }
      return "Starting from scratch - POP writes a fresh article.";
    },
  },
  methods: {
    select(value) {
      this.source.type = value;
      this.emit();
    },
    emit() {
      this.$emit("change");
    },
  },
};
</script>

<style scoped>
.content-source { margin-bottom: 18px; }
.cs-summary { font-size: 13px; color: var(--text-3, #6b7b7e); cursor: pointer; padding: 4px 0; }
.cs-summary-link { color: var(--pop-orange-deep, #b23d00); font-weight: 700; }
.cs-summary:hover .cs-summary-link { text-decoration: underline; }
.content-source.open .cs-summary { display: none; }
.cs-expanded { display: none; background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); padding: 16px 18px; box-shadow: var(--shadow-sm, 0 1px 2px rgba(15,20,25,.03)); }
.content-source.open .cs-expanded { display: block; }
.cs-seg { display: inline-flex; gap: 6px; background: var(--surface-2, #f7f7f7); border: 1px solid var(--border, #e4e4e4); border-radius: 11px; padding: 5px; flex-wrap: wrap; }
.cs-opt { display: inline-flex; align-items: center; gap: 8px; background: transparent; border: none; border-radius: 8px; padding: 9px 15px; cursor: pointer; font-family: inherit; color: var(--text-2, #014751); transition: background .12s, color .12s, box-shadow .12s; }
.cs-opt-ico { line-height: 0; color: var(--text-3, #6b7b7e); transition: color .12s; }
.cs-opt-ico :deep(svg) { width: 16px; height: 16px; }
.cs-opt-name { font-size: 13px; font-weight: 700; }
.cs-opt:hover { color: var(--text, #052e33); }
.cs-opt.selected { background: #fff; color: var(--pop-orange-deep, #b23d00); box-shadow: 0 1px 3px rgba(0,0,0,.1); }
.cs-opt.selected .cs-opt-ico { color: var(--pop-orange, #ff5700); }
.cs-helper { font-size: 12.5px; color: var(--text-3, #6b7b7e); margin: 11px 2px 0; }
.cs-panel { margin-top: 14px; }
.cs-textarea { width: 100%; box-sizing: border-box; min-height: 120px; resize: vertical; font-family: inherit; font-size: 13.5px; color: var(--text, #052e33); line-height: 1.5; border: 1px solid var(--border, #e4e4e4); border-radius: 9px; padding: 12px 14px; background: #fff; }
.cs-textarea:focus { outline: none; border-color: var(--pop-orange, #ff5700); box-shadow: 0 0 0 3px var(--pop-orange-soft, #ffeddd); }
.cs-url-row { display: flex; gap: 8px; }
.cs-url-input { flex: 1; font-family: inherit; font-size: 13.5px; color: var(--text, #052e33); border: 1px solid var(--border, #e4e4e4); border-radius: 9px; padding: 10px 13px; background: #fff; }
.cs-url-input:focus { outline: none; border-color: var(--pop-orange, #ff5700); box-shadow: 0 0 0 3px var(--pop-orange-soft, #ffeddd); }
.cs-url-status { font-size: 12.5px; margin-top: 10px; }
.cs-url-status.ok { color: #1c805d; font-weight: 600; }
.cs-url-status.ok strong { color: inherit; font-weight: 700; }
.cs-url-status.err { color: var(--pop-red, #ff3939); }
.spin { width: 12px; height: 12px; border: 2px solid var(--border, #e4e4e4); border-top-color: var(--pop-orange, #ff5700); border-radius: 50%; display: inline-block; animation: aiw2-cs-spin .7s linear infinite; }
@keyframes aiw2-cs-spin { to { transform: rotate(360deg); } }
</style>
